export type ScheduleMode =
  | 'interval'
  | 'hourly'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'monthly'
  | 'nth';

export type IntervalUnit = 'minute' | 'hour' | 'day';

export interface ScheduleParams {
  mode: ScheduleMode;
  hour: number;
  minute: number;
  days: number[]; // 1=Mon .. 7=Sun
  dom: number;
  intervalUnit: IntervalUnit;
  intervalN: number;
  nth: number; // 1..5, the nth occurrence of `nthDow` in the month
  nthDow: number; // 1=Mon .. 7=Sun, matching `days`
  rawCron?: string;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Short labels suit the compact pickers; prose reads better spelled out.
const DAYS_FULL = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const pad2 = (n: number): string => String(n).padStart(2, '0');

export const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);
export const MINUTES = Array.from({ length: 60 }, (_, i) => i);
export const DAY_LABELS = DAYS;

export const MINUTE_INTERVAL_OPTIONS = [5, 10, 15, 20, 30, 45];
export const HOUR_INTERVAL_OPTIONS = [1, 2, 3, 4, 6, 8, 12];
// 1 is omitted (that is `daily`, which also lets the user pick a time) and 7 is
// omitted deliberately: cron `*/7` on day-of-month is NOT weekly -- it drifts
// across weekdays and restarts on the 1st. Use the `weekly` mode for that.
export const DAY_INTERVAL_OPTIONS = [2, 3, 4, 5, 6, 10];

export const NTH_OPTIONS = [1, 2, 3, 4, 5];
const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];

/** "1st".."5th", for compact pickers. */
export const ordinalShort = (n: number): string =>
  ['1st', '2nd', '3rd', '4th', '5th'][n - 1] ?? `${n}th`;

/** "First".."Fifth", for prose. */
export const ordinalWord = (n: number): string => ORDINALS[n - 1] ?? `${n}th`;

// The 7-day window that isolates the nth occurrence of a weekday. Temporal ANDs
// day-of-month with day-of-week (standard Vixie cron ORs them), so pairing a
// window with a weekday yields exactly one date per month -- verified against
// the server: `0 9 1-7 * 5` fires Sep 4, Oct 2, Nov 6, Dec 4 2026.
const NTH_WINDOWS: Record<number, [number, number]> = {
  1: [1, 7],
  2: [8, 14],
  3: [15, 21],
  4: [22, 28],
  5: [29, 31],
};

export const nthWindow = (nth: number): [number, number] =>
  NTH_WINDOWS[nth] ?? NTH_WINDOWS[1];

/**
 * How many of the coming months actually contain the nth occurrence.
 *
 * The 1st through 4th of any weekday exist in every month (all months have at
 * least 28 days), but a 5th only exists when the month is long enough to reach
 * it -- so those schedules silently skip months. Verified against Temporal:
 * `0 9 29-31 * 1` fires Nov 30 2026, then not again until Mar 29 2027.
 *
 * @param nth Which occurrence, 1..5.
 * @param nthDow Weekday, 1=Mon..7=Sun.
 * @param from Month to start counting from. Defaults to now.
 * @param months How many months to examine. Defaults to 12.
 * @returns The number of months containing the occurrence, and how many were
 *   examined.
 */
export function nthCoverage(
  nth: number,
  nthDow: number,
  from: Date = new Date(),
  months: number = 12,
): { hits: number; total: number } {
  // getUTCDay() is 0=Sun..6=Sat; the builder speaks 1=Mon..7=Sun.
  const targetDow = nthDow === 7 ? 0 : nthDow;
  let hits = 0;
  for (let i = 0; i < months; i++) {
    // Local accessors so "the next 12 months" means the viewer's months; the
    // Date.UTC calls below are pure calendar arithmetic on those y/m values.
    const y = from.getFullYear();
    const m = from.getMonth() + i; // Date.UTC rolls overflow into the year
    const firstDow = new Date(Date.UTC(y, m, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const firstHit = 1 + ((targetDow - firstDow + 7) % 7);
    if (firstHit + (nth - 1) * 7 <= daysInMonth) hits++;
  }
  return { hits, total: months };
}

/**
 * Warning text for an nth-weekday schedule that cannot run every month.
 *
 * @param nth Which occurrence, 1..5.
 * @param nthDow Weekday, 1=Mon..7=Sun.
 * @param options.from Month to count from. Defaults to now.
 * @param options.months How many months to examine. Defaults to 12.
 * @returns The warning, or null when the occurrence exists in every month
 *   examined (so 1st-4th selections never raise a false alarm).
 */
export function nthCoverageNotice(
  nth: number,
  nthDow: number,
  options: { from?: Date; months?: number } = {},
): string | null {
  const { from = new Date(), months = 12 } = options;
  const { hits, total } = nthCoverage(nth, nthDow, from, months);
  if (hits >= total) return null;
  const label = `${ordinalShort(nth)} ${DAYS_FULL[nthDow - 1]}`;
  return (
    `Not every month has a ${label}. This schedule runs in ${hits} of the ` +
    `next ${total} months and skips the others.`
  );
}

// Shown whenever a day interval is selected. Temporal ANDs the cron fields and
// evaluates `*/N` within each month, so the run count restarts on the 1st and
// the interval spanning a month boundary can be shorter than N days.
export const DAY_INTERVAL_NOTICE =
  'Day intervals are counted from the 1st of each month, so the gap across a ' +
  'month boundary can be shorter than the interval you picked.';

/** Selectable interval values for a given unit. */
export function intervalOptionsFor(unit: IntervalUnit): number[] {
  if (unit === 'minute') return MINUTE_INTERVAL_OPTIONS;
  if (unit === 'hour') return HOUR_INTERVAL_OPTIONS;
  return DAY_INTERVAL_OPTIONS;
}

export function buildCron(params: ScheduleParams): string {
  const {
    mode,
    hour,
    minute,
    days,
    dom,
    intervalUnit,
    intervalN,
    nth,
    nthDow,
  } = params;

  if (mode === 'interval') {
    if (intervalUnit === 'minute') return `*/${intervalN} * * * *`;
    if (intervalUnit === 'hour') return `0 */${intervalN} * * *`;
    // Day intervals step the day-of-month field: the 1st, 1+N, 1+2N... of each
    // month. Unlike minute/hour steps they carry a time-of-day, since firing at
    // midnight is almost never what the user meant.
    return `${minute} ${hour} */${intervalN} * *`;
  }
  if (mode === 'hourly') return `${minute} * * * *`;
  if (mode === 'daily') return `${minute} ${hour} * * *`;
  if (mode === 'weekdays') return `${minute} ${hour} * * 1-5`;
  if (mode === 'weekly') {
    const list = days.length ? days.join(',') : '*';
    return `${minute} ${hour} * * ${list}`;
  }
  if (mode === 'monthly') return `${minute} ${hour} ${dom} * *`;
  if (mode === 'nth') {
    const [lo, hi] = nthWindow(nth);
    return `${minute} ${hour} ${lo}-${hi} * ${nthDow}`;
  }
  return `${minute} ${hour} * * *`;
}

export function humanCron(params: ScheduleParams): string {
  if (params.rawCron) return `Custom: ${params.rawCron}`;
  const {
    mode,
    hour,
    minute,
    days,
    dom,
    intervalUnit,
    intervalN,
    nth,
    nthDow,
  } = params;
  const t = `${pad2(hour)}:${pad2(minute)}`;

  if (mode === 'interval') {
    if (intervalUnit === 'day') return `Every ${intervalN} days at ${t}`;
    return `Every ${intervalN} ${intervalUnit}${intervalN === 1 ? '' : 's'}`;
  }
  if (mode === 'hourly') return `Every hour at :${pad2(minute)}`;
  if (mode === 'daily') return `Every day at ${t}`;
  if (mode === 'weekdays') return `Weekdays at ${t}`;
  if (mode === 'weekly') {
    if (!days.length) return `Weekly at ${t}`;
    const lbls = days.map((d) => DAYS[d - 1]);
    return `${lbls.join(', ')} at ${t}`;
  }
  if (mode === 'monthly') return `Monthly on day ${dom} at ${t}`;
  if (mode === 'nth') {
    return `${ordinalWord(nth)} ${DAYS[nthDow - 1]} of every month at ${t}`;
  }
  return `Daily at ${t}`;
}

export function nextRuns(
  params: ScheduleParams,
  count: number = 5,
  from: Date = new Date(),
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): Date[] {
  if (params.rawCron) return [];
  const {
    mode,
    hour,
    minute,
    days,
    dom,
    intervalUnit,
    intervalN,
    nth,
    nthDow,
  } = params;
  const out: Date[] = [];

  // Minute and hour intervals are true fixed periods, so they can be stepped
  // off the epoch. Day intervals are cron day-of-month steps that restart every
  // month, so they fall through to the calendar walk below instead.
  if (mode === 'interval' && intervalUnit !== 'day') {
    const cursor = new Date(from);
    cursor.setSeconds(0, 0);
    const stepMs =
      intervalUnit === 'minute' ? intervalN * 60_000 : intervalN * 3_600_000;
    // Align to next interval boundary
    const epochMin = Math.floor(cursor.getTime() / 60_000);
    const intervalMin = intervalUnit === 'minute' ? intervalN : intervalN * 60;
    const nextBoundary = (Math.floor(epochMin / intervalMin) + 1) * intervalMin;
    cursor.setTime(nextBoundary * 60_000);
    for (let i = 0; i < count; i++) {
      out.push(new Date(cursor));
      cursor.setTime(cursor.getTime() + stepMs);
    }
    return out;
  }

  // For fixed-time modes, create a date in the target timezone and step by day
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    hour12: false,
  });

  const parseParts = (d: Date) => {
    const parts = fmt.formatToParts(d);
    const get = (type: string) =>
      parts.find((p) => p.type === type)?.value ?? '';
    const h = parseInt(get('hour'), 10) % 24;
    const m = parseInt(get('minute'), 10);
    const weekdayStr = get('weekday');
    const dayOfMonth = parseInt(get('day'), 10);
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dow = weekdays.indexOf(weekdayStr);
    const w = dow === 0 ? 7 : dow;
    return { h, m, w, dayOfMonth };
  };

  // Step size: hourly checks every hour, others check every day
  const stepMs = mode === 'hourly' ? 3_600_000 : 86_400_000;
  const cursor = new Date(from);
  cursor.setSeconds(0, 0);
  const maxSteps = mode === 'hourly' ? 8760 : 400;

  for (let steps = 0; out.length < count && steps < maxSteps; steps++) {
    // The cursor carries `from`'s time-of-day, so blindly stepping by whole
    // hours/days would never land on the scheduled minute. Align each
    // candidate to the scheduled time in the target timezone first, then
    // check the day-based fields against the aligned candidate.
    const parts = parseParts(cursor);
    const deltaMinutes =
      mode === 'hourly'
        ? minute - parts.m
        : (hour - parts.h) * 60 + (minute - parts.m);
    const candidate = new Date(cursor.getTime() + deltaMinutes * 60_000);
    const { h, m, w, dayOfMonth } = parseParts(candidate);

    let match = false;
    if (mode === 'hourly') {
      match = m === minute;
    } else if (mode === 'daily') {
      match = h === hour && m === minute;
    } else if (mode === 'weekdays') {
      match = h === hour && m === minute && w >= 1 && w <= 5;
    } else if (mode === 'weekly') {
      match =
        h === hour && m === minute && (days.length ? days.includes(w) : true);
    } else if (mode === 'interval') {
      // cron `*/N` on day-of-month matches 1, 1+N, 1+2N... inside each month
      match = h === hour && m === minute && (dayOfMonth - 1) % intervalN === 0;
    } else if (mode === 'monthly') {
      match = h === hour && m === minute && dayOfMonth === dom;
    } else if (mode === 'nth') {
      // Mirrors the cron AND: the weekday must fall inside the nth window.
      const [lo, hi] = nthWindow(nth);
      match =
        h === hour &&
        m === minute &&
        w === nthDow &&
        dayOfMonth >= lo &&
        dayOfMonth <= hi;
    }

    const isFutureAndNew =
      candidate.getTime() > from.getTime() &&
      (out.length === 0 || candidate.getTime() > out[out.length - 1].getTime());
    if (match && isFutureAndNew) out.push(candidate);
    cursor.setTime(cursor.getTime() + stepMs);
  }
  return out;
}

export function fmtRel(d: Date, now: Date = new Date()): string {
  const diff = (d.getTime() - now.getTime()) / 1000;
  if (diff < 0) {
    const a = -diff;
    if (a < 60) return `${Math.floor(a)}s ago`;
    if (a < 3600) return `${Math.floor(a / 60)}m ago`;
    if (a < 86400) return `${Math.floor(a / 3600)}h ago`;
    return `${Math.floor(a / 86400)}d ago`;
  }
  if (diff < 60) return `in ${Math.floor(diff)}s`;
  if (diff < 3600) return `in ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `in ${Math.floor(diff / 3600)}h`;
  return `in ${Math.floor(diff / 86400)}d`;
}

export function fmtDateTime(d: Date, timezone?: string): string {
  return d.toLocaleString(undefined, {
    timeZone: timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    // Without this the hour reads as the viewer's own clock whatever zone
    // it was actually rendered in, which is wrong by the offset and gives
    // no hint that it is.
    timeZoneName: 'short',
  });
}

/**
 * Human cadence for any schedule, cron or one-shot.
 *
 * Shared so the Schedules page and the workflow sidebar can't drift. Takes a
 * structural shape rather than the WorkflowSchedule type to keep this module
 * free of a service-layer import.
 *
 * Before one-shots existed, callers passed `cron_expression` straight to
 * `parseCronToMode`. That field is now null on one-shots, which rendered an
 * empty cadence — legible only because the call sites happened to be wrapped
 * in try/catch.
 */
export function scheduleCadenceLabel(schedule: {
  schedule_type?: string;
  cron_expression?: string | null;
  run_at?: string | null;
  timezone?: string;
}): string {
  if (schedule.schedule_type === 'one_shot') {
    if (!schedule.run_at) return 'Once';
    const at = new Date(schedule.run_at);
    if (Number.isNaN(at.getTime())) return 'Once';
    // Deliberately not `schedule.timezone`: run_at is an absolute instant,
    // so the viewer's own zone is the honest lens. A cron expression is the
    // opposite -- "0 8 * * *" means 08:00 where the schedule lives -- which
    // is why only this branch drops the stored zone.
    return `Once, ${fmtDateTime(at)}`;
  }

  if (!schedule.cron_expression) return 'No cadence set';

  try {
    return humanCron(parseCronToMode(schedule.cron_expression));
  } catch {
    return schedule.cron_expression;
  }
}

export function parseCronToMode(cron: string): ScheduleParams {
  const parts = cron.trim().split(/\s+/);
  const defaults: ScheduleParams = {
    mode: 'daily',
    hour: 8,
    minute: 0,
    days: [1, 2, 3, 4, 5],
    dom: 1,
    intervalUnit: 'hour',
    intervalN: 6,
    nth: 1,
    nthDow: 5,
  };

  if (parts.length !== 5) return { ...defaults, rawCron: cron };

  const [minField, hourField, domField, monthField, dowField] = parts;

  // If month is restricted, we can't represent it in the UI — preserve raw cron
  if (monthField !== '*') return { ...defaults, rawCron: cron };

  // Interval: */N * * * * or 0 */N * * *
  // A bare `*` minute is the same schedule as `*/1`: the scheduler backend
  // drops a step of one as redundant, so that is the form a saved 1-minute
  // interval comes back in.
  const minuteStep = minField === '*' ? 1 : parseInt(minField.slice(2), 10);
  if (
    (minField === '*' || minField.startsWith('*/')) &&
    hourField === '*' &&
    domField === '*' &&
    dowField === '*' &&
    !isNaN(minuteStep)
  ) {
    return {
      ...defaults,
      mode: 'interval',
      intervalUnit: 'minute',
      intervalN: minuteStep,
    };
  }
  if (
    minField === '0' &&
    hourField.startsWith('*/') &&
    domField === '*' &&
    dowField === '*'
  ) {
    const n = parseInt(hourField.slice(2), 10);
    if (!isNaN(n)) {
      return {
        ...defaults,
        mode: 'interval',
        intervalUnit: 'hour',
        intervalN: n,
      };
    }
  }

  const min = parseInt(minField, 10);
  const hr = parseInt(hourField, 10);

  // Hourly: N * * * *
  if (
    !isNaN(min) &&
    hourField === '*' &&
    domField === '*' &&
    dowField === '*'
  ) {
    return { ...defaults, mode: 'hourly', minute: min };
  }

  if (isNaN(min) || isNaN(hr)) return { ...defaults, rawCron: cron };

  // Day interval: M H */N * *
  // Must precede the monthly branch below, whose day-of-month check only
  // accepts plain integers and would otherwise send this to raw cron.
  if (domField.startsWith('*/') && dowField === '*') {
    const n = parseInt(domField.slice(2), 10);
    if (!isNaN(n)) {
      return {
        ...defaults,
        mode: 'interval',
        intervalUnit: 'day',
        intervalN: n,
        hour: hr,
        minute: min,
      };
    }
  }

  // Nth weekday: M H <window> * <dow>, e.g. `0 9 1-7 * 5` = first Friday.
  // Neither the weekly branch (needs dom `*`) nor the monthly branch (needs dow
  // `*`) claims this shape, so it would otherwise fall through to raw cron.
  if (/^\d+-\d+$/.test(domField) && /^\d+$/.test(dowField)) {
    const [lo, hi] = domField.split('-').map((v) => parseInt(v, 10));
    const nthMatch = NTH_OPTIONS.find((n) => {
      const [wLo, wHi] = nthWindow(n);
      return wLo === lo && wHi === hi;
    });
    const dow = parseInt(dowField, 10);
    if (nthMatch && dow >= 0 && dow <= 7) {
      return {
        ...defaults,
        mode: 'nth',
        hour: hr,
        minute: min,
        nth: nthMatch,
        // cron accepts 0 and 7 for Sunday (both verified on Temporal); the
        // builder speaks 1=Mon..7=Sun, so fold 0 up to 7.
        nthDow: dow === 0 ? 7 : dow,
      };
    }
  }

  // Weekdays: M H * * 1-5
  if (domField === '*' && dowField === '1-5') {
    return { ...defaults, mode: 'weekdays', hour: hr, minute: min };
  }

  // Weekly: M H * * dow-list (normalize Sunday 0 → 7)
  if (domField === '*' && dowField !== '*') {
    const tokens = dowField.split(',');
    // If any token contains a range (e.g. 2-4, 1-5), fall back to raw cron
    if (tokens.some((t) => t.includes('-'))) {
      return { ...defaults, rawCron: cron };
    }
    const dayList = tokens
      .map((d) => parseInt(d, 10))
      .filter((d) => !isNaN(d))
      .map((d) => (d === 0 ? 7 : d));
    return {
      ...defaults,
      mode: 'weekly',
      hour: hr,
      minute: min,
      days: dayList,
    };
  }

  // Monthly: M H dom * * (only plain integers, not ranges/lists)
  if (domField !== '*' && dowField === '*') {
    if (/^\d+$/.test(domField)) {
      const day = parseInt(domField, 10);
      return { ...defaults, mode: 'monthly', hour: hr, minute: min, dom: day };
    }
    return { ...defaults, rawCron: cron };
  }

  // Daily: M H * * *
  if (domField === '*' && dowField === '*') {
    return { ...defaults, mode: 'daily', hour: hr, minute: min };
  }

  return { ...defaults, hour: hr, minute: min, rawCron: cron };
}
